import { Component, OnInit, AfterViewInit, ViewChild, OnDestroy } from '@angular/core';
import { PaymentService } from "./payment.service";
import { ChartValue } from "../dashboard/chart/chart-value.model";
import { DateHelperService } from "../core/services/date-helper.service";
import { DropdownValue } from "../shared/dropdown.model";
import { FilterService } from "../core/services/filter.service";
import { SortModel } from "../shared/sort.model";
import { IPaymentModel, IPaymentChartModel } from "./payment.model";
import { PaymentSortField } from "./payment-sort.enum";
import { PageSizeSelectionComponent } from "../shared/page-size-selection.component";
import { PublisherGroupSelectionComponent } from '../shared/publisher-group-selection.component';
import { ClaimsService } from "../core/services/claims.service";
import { BarChartOptions } from "../dashboard/chart/bar-chart-options.model";
import { IPublisherGroup } from "../shared/publisher-group.model";
import { CustomHorizontalScrollService } from "../shared/custom-horizontal-scroll.service";
import { SortService } from '../shared/sort.service';

@Component({
    moduleId: module.id,
    templateUrl: 'payment.component.html'
})
export class PaymentComponent implements OnInit, AfterViewInit, OnDestroy {
    errorMessage: any;
    chartData: IPaymentChartModel[];
    chartValues: ChartValue[];
    data: IPaymentModel[];
    pageNumber: number = 1;
    totalCount: number;
    pageSize: number = 10;
    useLightClass: boolean = true;
    publisherGroupNameSort: SortModel = new SortModel(PaymentSortField.PublisherGroupName, SortModel.disabledIcon);
    revenueMonthSort: SortModel = new SortModel(PaymentSortField.RevenueMonth, SortModel.disabledIcon);
    amountSort: SortModel = new SortModel(PaymentSortField.Amount, SortModel.disabledIcon);
    statusSort: SortModel = new SortModel(PaymentSortField.Status, SortModel.ascIcon);
    paymentDateSort: SortModel = new SortModel(PaymentSortField.PaymentDate, SortModel.disabledIcon);
    showAdminActionButtons: boolean = false;
    chartOptions: BarChartOptions;
    toolTipText = "{2} {0} ";
    showChartOverlay: boolean = true;
    showOverlay: boolean = true;
    private publisherGroupIds: string;
    private sortField: SortModel;
    private sortDirectionAsc: boolean = true;
    private inXhrGetPayments: any;
    private inXhrGetChart: any;

    @ViewChild("pageSizeSelection") pageSizeSelectionComponent: PageSizeSelectionComponent
    @ViewChild("publisherGroupSelector") publisherGroupSelectedItemsComponent: PublisherGroupSelectionComponent

    constructor(private paymentService: PaymentService,
        private dateHelper: DateHelperService,
        private filterItemsService: FilterService,
        private claimsService: ClaimsService,
        private scrollService: CustomHorizontalScrollService,
        private sortService: SortService) {
        this.chartOptions = new BarChartOptions();
    }

    private getChartData() {
        this.showChartOverlay = true;
        if (this.inXhrGetChart) {
            this.inXhrGetChart.unsubscribe();
        }
        this.inXhrGetChart = this.paymentService.getChartData()
            .subscribe((res: any) => {
                this.chartData = <Array<IPaymentChartModel>>res.json()
            }, error => this.errorMessage = error,
                () => this.onCompleteGetChartData())
    }

    public getPaymentlist(page: number) {
        if (this.inXhrGetPayments) {
            this.inXhrGetPayments.unsubscribe();
        }
        this.showOverlay = true;
        this.inXhrGetPayments = this.paymentService.getPayments(this.sortField.fieldId, this.sortDirectionAsc, page, this.pageSize, this.publisherGroupIds)
            .subscribe((res: any) => {
                this.totalCount = res.json().total;
                this.pageNumber = page;
                this.data = <Array<IPaymentModel>>res.json().data;
            }, error => this.errorMessage = error, () => this.showOverlay = false)
    }

    private onCompleteGetChartData(): void {
        this.chartValues = new Array<ChartValue>();
        let groups: IPublisherGroup[] = new Array<IPublisherGroup>();
        for (let item of this.chartData) {
            if (groups.findIndex(s => s.id == item.publisherGroup.id) === -1)
                groups.push(item.publisherGroup);
        }

        if (groups.length > 0) {
            for (let group of groups) {
                let chartValue = new ChartValue(group.name);
                chartValue.identifier = group.id;
                let revenues = this.chartData.filter(x => x.publisherGroup.id == group.id);
                for (let item of revenues) {
                    let ticks = this.dateHelper.getTicks(item.payed.dateUtc);
                    let index = chartValue.data.findIndex(s => s[0] === ticks);
                    if (index !== -1)
                        chartValue.data[index][1] = chartValue.data[index][1] + item.payed.amount;
                    else
                        chartValue.data.push([ticks, item.payed.amount]);
                }
                this.chartValues.push(chartValue);
            }
        }
        for (let chart of this.chartValues) {
            chart.data = chart.data.sort((a: number, b: number) => {
                return a[0] - b[0];
            })
        }
        this.showChartOverlay = false;
        this.toggleHorizontalScroll();
    }


    ngOnInit() {
        this.publisherGroupIds = this.filterItemsService.getPublisherGroups().map(s => s.id).join();
        this.sortField = this.statusSort;
        this.showAdminActionButtons = this.claimsService.getUserTypeId() === 1;
        this.chartOptions.xaxis = {
            mode: "time",
            timeformat: "%b",
        };
        this.chartOptions.series = {
            stack: true,
            bars: {
                show: true,
                barWidth: 1296000000,//1000 * 60 * 60 * 24 * 15
                align: "center",
                order: 1
            },
            points: {
                show: true,
                radius: 0,
                symbol: 'square'
            }
        };
        this.chartOptions.tooltip_top = -64;
        this.chartOptions.tooltip_left = -10;
        this.chartOptions.yaxis = { tickFormatter: this.chartOptions.getMonetaryShortFormat };
        this.chartOptions.tooltipCurrency = '$ ';

        this.getChartData();
    }

    onPageSizeChanged(item: DropdownValue) {
        this.pageSize = item.value;
        this.pageNumber = 1;
        this.getPaymentlist(this.pageNumber);
    }

    onSortChanged(sort: SortModel) {
        let sortResult = this.sortService.toggleTableColumnSort(this.sortField, this.sortDirectionAsc, sort);
        this.sortField = sortResult.existingSortModel;
        sort = sortResult.selectedSortModel;
        this.sortDirectionAsc = sortResult.sortDiresctionAsc;

        this.getPaymentlist(1);
    }

    ngAfterViewInit() {
        this.pageSize = this.pageSizeSelectionComponent.defaultPageSizeSelection.value;
        this.publisherGroupSelectedItemsComponent.setFirstItem();
        this.getPaymentlist(1);
    }

    onPublisherGroupValueChanged(item: DropdownValue) {
        if (item.value === 0)
            this.publisherGroupIds = this.filterItemsService.getPublisherGroups().map(s => s.id).join();
        else
            this.publisherGroupIds = item.value.toString();
        this.getPaymentlist(1);
    }

    onPaymentChanged() {
        this.getChartData();
        this.getPaymentlist(this.pageNumber);
    }

    onPaymentCreated() {
        this.getChartData();
        this.getPaymentlist(this.pageNumber);
    }

    onResize(event) {
        this.toggleHorizontalScroll();
    }

    onPublisherGroupsClicked(event) {
        $(".configurable-dropdown .list").css("left", function () {
            return $(this).parent().position().left;
        });
        $(".configurable-dropdown .list").css("top", function () {
            return $(this).parent().position().top;
        });
    }

    private toggleHorizontalScroll() {
        let self = this;
        setTimeout(function () {
            self.scrollService.toggleHorizontalScroll();
        }, 10);
    }

    ngOnDestroy() {
        if (this.inXhrGetPayments) {
            this.inXhrGetPayments.unsubscribe();
        }
        if (this.inXhrGetChart) {
            this.inXhrGetChart.unsubscribe();
        }
        this.scrollService.toggleHorizontalScroll(false);
    }
}


// WEBPACK FOOTER //
// ./src/app/payment/payment.component.ts